import React, { useState, useEffect, useCallback } from 'react';
import emailOutreachApi from '../api/emailOutreachApi';
import ReplyComposerModal from './ReplyComposerModal';

const DISPOSITIONS = [
  'all',
  'interested',
  'meeting_request',
  'referral',
  'not_now',
  'objection',
  'out_of_office',
  'wrong_contact',
  'unsubscribe',
  'other',
];

function ReplyTriageTab({ canManage, mailboxes = [] }) {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedReplyId, setExpandedReplyId] = useState(null);
  const [replyModalTarget, setReplyModalTarget] = useState(null);
  const [successToast, setSuccessToast] = useState(null);

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      setError(null);
      await emailOutreachApi.syncAllMailboxes();
      setTimeout(async () => {
        await loadReplies();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to sync inbound replies');
    } finally {
      setTimeout(() => {
        setSyncing(false);
      }, 1500);
    }
  };

  const loadReplies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await emailOutreachApi.listReplies({ classification: filter });
      if (res.success && Array.isArray(res.data)) {
        setReplies(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load inbound replies');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadReplies();
  }, [loadReplies]);

  const handleReviewClassification = async (id, newClassification) => {
    try {
      setUpdatingId(id);
      setError(null);
      const res = await emailOutreachApi.reviewReply(id, newClassification);
      if (res.success) {
        setReplies((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  reviewed_classification: newClassification,
                  effective_classification: newClassification,
                  reviewed_at: new Date().toISOString(),
                }
              : r
          )
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update review');
    } finally {
      setUpdatingId(null);
    }
  };

  const getPillClass = (classification) => {
    switch (classification) {
      case 'interested':
      case 'meeting_request':
        return 'eo-pill-positive';
      case 'referral':
      case 'not_now':
        return 'eo-pill-neutral';
      case 'objection':
      case 'wrong_contact':
        return 'eo-pill-warning';
      case 'unsubscribe':
        return 'eo-pill-danger';
      default:
        return 'eo-pill-default';
    }
  };

  return (
    <div className="eo-tab-content">
      <div className="eo-tab-header">
        <div>
          <h3 className="eo-section-title">Inbound Reply Classification & Triage</h3>
          <p className="eo-section-desc">
            AI automatically categorizes inbound email replies, estimates confidence, and suggests
            next actions. Operators can review or override classifications, with provenance strictly
            preserved.
          </p>
        </div>
        <div className="eo-filter-bar">
          <label className="eo-filter-label">Filter by Disposition:</label>
          <select
            className="eo-select eo-select-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            {DISPOSITIONS.map((d) => (
              <option key={d} value={d}>
                {d === 'all' ? 'All Dispositions' : d.replace(/_/g, ' ').toUpperCase()}
              </option>
            ))}
          </select>
          {canManage && (
            <button
              className="eo-btn eo-btn-sm eo-btn-primary"
              onClick={handleSyncAll}
              disabled={syncing || loading}
              title="Poll connected mailboxes and fetch latest inbound replies"
            >
              <i className={`fas fa-sync-alt ${syncing ? 'fa-spin' : ''}`}></i>
              <span>{syncing ? ' Syncing...' : ' Sync Inbound Emails'}</span>
            </button>
          )}
          <button
            className="eo-btn eo-btn-sm eo-btn-secondary"
            onClick={loadReplies}
            disabled={loading}
            title="Refresh list"
          >
            <i className={`fas fa-redo ${loading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {successToast && (
        <div className="eo-toast-banner success">
          <div className="eo-toast-body">
            <i className="fas fa-check-circle"></i>
            <span>{successToast}</span>
          </div>
          <button className="eo-toast-close" onClick={() => setSuccessToast(null)}>
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="eo-alert eo-alert-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button className="eo-alert-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="eo-loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading inbound replies...</span>
        </div>
      ) : replies.length === 0 ? (
        <div className="eo-empty-state">
          <div className="eo-empty-icon">
            <i className="fas fa-inbox"></i>
          </div>
          <h4>No Replies Found</h4>
          <p>
            {filter !== 'all'
              ? `No inbound replies match the "${filter}" disposition.`
              : 'Synced incoming emails and categorized replies will appear here.'}
          </p>
        </div>
      ) : (
        <div className="eo-replies-table-wrapper">
          <table className="eo-table">
            <thead>
              <tr>
                <th>Contact & Subject</th>
                <th>AI Classification</th>
                <th>Recommended Action</th>
                <th>Received</th>
                {canManage && <th style={{ minWidth: '220px' }}>Actions & Review</th>}
              </tr>
            </thead>
            <tbody>
              {replies.map((reply) => {
                const effCls = reply.effective_classification || reply.classification || 'other';
                const isOverridden = Boolean(reply.reviewed_classification);
                const confPercent =
                  reply.classification_confidence != null
                    ? `${Math.round(reply.classification_confidence * 100)}%`
                    : null;
                const isExpanded = expandedReplyId === reply.id;

                return (
                  <React.Fragment key={reply.id}>
                    <tr
                      className={`eo-reply-row ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => setExpandedReplyId(isExpanded ? null : reply.id)}
                    >
                      <td>
                        <div className="eo-contact-col">
                          <strong className="eo-contact-sender">
                            {reply.from_email || 'Unknown Sender'}
                          </strong>
                          <span className="eo-subject-preview">
                            <i className="fas fa-reply eo-reply-icon"></i>
                            {reply.subject || '(no subject)'}
                          </span>
                          {reply.snippet && <p className="eo-snippet-text">{reply.snippet}</p>}
                        </div>
                      </td>
                      <td>
                        <div className="eo-classification-col">
                          <span className={`eo-disposition-pill ${getPillClass(effCls)}`}>
                            {effCls.replace(/_/g, ' ')}
                            {confPercent && <small> ({confPercent})</small>}
                          </span>
                          {isOverridden && (
                            <span
                              className="eo-badge-reviewed"
                              title={`Corrected by operator (AI: ${reply.classification})`}
                            >
                              <i className="fas fa-user-check"></i> Reviewed
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="eo-recommended-action">
                          {reply.recommended_action || 'No action suggested'}
                        </span>
                      </td>
                      <td>
                        <span className="eo-date-text">
                          {reply.received_at
                            ? new Date(reply.received_at).toLocaleDateString()
                            : 'Recent'}
                        </span>
                      </td>
                      {canManage && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <div
                            className="eo-review-action-cell"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                          >
                            <button
                              className="eo-btn eo-btn-xs eo-btn-primary"
                              onClick={() => setReplyModalTarget({ ...reply, autoGenerate: false })}
                              title="Reply with template, custom copy, or AI draft"
                            >
                              <i className="fas fa-reply"></i> Reply
                            </button>
                            <select
                              className="eo-select-inline"
                              value={effCls}
                              disabled={updatingId === reply.id}
                              onChange={(e) => handleReviewClassification(reply.id, e.target.value)}
                              title="Correct or review disposition"
                            >
                              {DISPOSITIONS.filter((d) => d !== 'all').map((d) => (
                                <option key={d} value={d}>
                                  {d.replace(/_/g, ' ')}
                                </option>
                              ))}
                            </select>
                            {updatingId === reply.id && (
                              <i className="fas fa-spinner fa-spin eo-spinner-sm"></i>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {isExpanded && reply.snippet && (
                      <tr className="eo-expanded-row">
                        <td colSpan={canManage ? 5 : 4}>
                          <div className="eo-expanded-content">
                            <h5>Message Details</h5>
                            <p className="eo-body-full">{reply.snippet}</p>
                            <div className="eo-meta-row">
                              <span>
                                <strong>Original AI Disposition:</strong>{' '}
                                {reply.classification || 'none'}
                              </span>
                              <span>
                                <strong>Confidence Score:</strong> {confPercent || 'N/A'}
                              </span>
                              {reply.reviewed_at && (
                                <span>
                                  <strong>Reviewed:</strong>{' '}
                                  {new Date(reply.reviewed_at).toLocaleString()}
                                </span>
                              )}
                            </div>
                            {canManage && (
                              <div className="eo-expanded-actions" style={{ marginTop: '12px' }}>
                                <button
                                  className="eo-btn eo-btn-sm eo-btn-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReplyModalTarget({ ...reply, autoGenerate: true });
                                  }}
                                >
                                  <i className="fas fa-magic"></i> Draft AI Reply
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {replyModalTarget && (
        <ReplyComposerModal
          isOpen={Boolean(replyModalTarget)}
          reply={replyModalTarget}
          mailboxes={mailboxes}
          onClose={() => setReplyModalTarget(null)}
          onSuccess={(data) => {
            setSuccessToast(`Reply sent successfully to ${data.recipient || 'prospect'}!`);
            setTimeout(() => setSuccessToast(null), 6000);
            loadReplies();
          }}
        />
      )}
    </div>
  );
}

export default ReplyTriageTab;
